// Paramètres et confidentialité — quatrième écran migré, et le plus dense.
//
// C'est le premier qui a de l'ÉTAT à tenir : six réglages, modifiables d'un
// geste, enregistrés sans bouton de validation. Dans le monde d'avant, chaque
// bascule redessinait la liste entière à la main et il fallait penser à
// remettre l'ancienne valeur en cas d'échec. Ici l'état est la source, et
// l'affichage en découle.
//
// Honnêteté conservée telle quelle : tant que les règles serveur ne sont pas
// toutes déployées, ces réglages sont appliqués par l'application. La page le
// dit au membre plutôt que de le lui laisser croire.

import { useState, useRef, useEffect } from "react";
import Store from "../../store.js";
import { toast } from "../../ui.js";
import { sonsActifs, reglerSons, jouer } from "../../sons.js";
import { CONFIDENTIALITE_DEFAUT, confidentialiteDe } from "../../securite.js";
import { APP } from "../../config.js";
import { ouvrirListeBloques } from "../../views/blocages.js";
import { exporterMesDonnees, supprimerMonCompte } from "../../views/compte.js";
import { editerProfil } from "../../views/profil-editer.js";
import { changerMotDePasse } from "../../views/parametres-mdp.js";
import * as Alertes from "../../alertes.js";
import Icone from "../Icone.jsx";

const REGLAGES = [
  {
    cle: "publications",
    titre: "Qui peut voir mes publications",
    aide: "S'applique aux nouvelles publications et aux statuts. Ce qui est déjà publié garde l'audience qu'il avait ce jour-là.",
    choix: [
      { valeur: "tous", label: "Tous les membres" },
      { valeur: "amis", label: "Mes amis" },
    ],
  },
  {
    cle: "details",
    titre: "Qui peut voir mon église, ma ville et mes amis",
    aide: "Votre nom et votre photo restent visibles : c'est ce qui permet de vous reconnaître.",
    choix: [
      { valeur: "tous", label: "Tous les membres" },
      { valeur: "amis", label: "Mes amis" },
    ],
  },
  {
    cle: "demandes",
    titre: "Qui peut m'envoyer une demande d'amitié",
    aide: "« Amis d'amis » demande au moins une relation en commun.",
    choix: [
      { valeur: "tous", label: "Tous les membres" },
      { valeur: "amisDAmis", label: "Amis d'amis" },
    ],
  },
  {
    cle: "messages",
    titre: "Qui peut m'écrire en privé",
    aide: "Vos amis peuvent toujours vous écrire.",
    choix: [
      { valeur: "tous", label: "Tous les membres" },
      { valeur: "amis", label: "Mes amis" },
    ],
  },
  {
    cle: "annuaire",
    titre: "Apparaître dans l'annuaire et la recherche",
    aide: "Désactivé, seuls vos amis peuvent vous retrouver par votre nom.",
    interrupteur: true,
  },
  {
    cle: "presence",
    titre: "Afficher que je suis en ligne",
    aide: "Désactivé, votre appareil cesse d'annoncer votre présence — vous ne voyez plus non plus qui est connecté.",
    interrupteur: true,
  },
  /* ⚠️ L'AIDE DIT EXACTEMENT CE QUI EST PUBLIÉ, et pas un mot de plus.
     « Le jour et le mois » n'est pas une formule prudente, c'est la vérité
     technique : la fiche publique ne porte que `neLe` — « 08-19 » — et l'année
     n'en sort jamais. La date complète est restée dans `prive/contact`, où
     l'audit du 17 août 2026 l'a mise avec le téléphone et le genre. Quelqu'un
     doit pouvoir décider en lisant cette ligne, sans nous croire sur parole. */
  {
    cle: "anniversaire",
    titre: "Montrer mon anniversaire",
    aide: "Vos amis voient le jour et le mois le jour venu — jamais l'année, jamais votre âge. Votre date de naissance complète reste privée dans tous les cas.",
    interrupteur: true,
  },
  /* Le seul réglage qui part ÉTEINT. En dernier, sans emphase : c'est une
     possibilité offerte à qui la cherche, pas une invitation. */
  {
    cle: "abonnes",
    titre: "Autoriser les abonnés",
    aide: "D'autres membres peuvent alors suivre ce que vous ouvrez à tous, sans être vos amis — et sans limite de nombre. Ils ne voient jamais ce que vous réservez à vos amis. En le désactivant, vos abonnés sont retirés.",
    interrupteur: true,
  },
];

const etatBapteme = (moi) => {
  if (moi.validation === "valide") return <>attestation validée</>;
  if (moi.validation === "refuse")
    return <span className="etiquette etiquette--refuse">attestation refusée</span>;
  if (moi.validation === "en_attente")
    return <span className="etiquette etiquette--attente">en cours de vérification</span>;
  return <>—</>;
};

function Bascule({ actif }) {
  return (
    <span className={`bascule${actif ? " est-actif" : ""}`}>
      <span className="bascule__pouce" />
    </span>
  );
}

/* Un réglage qui ne s'applique plus.
   Un compte d'accueil publie toujours ouvert (voir `js/decouverte.js`). Laisser
   la question « Qui peut voir mes publications » avec « Mes amis » allumé
   ferait mentir l'écran : le membre croirait choisir quelque chose qui ne se
   produit pas. On le DIT, plutôt que de le laisser croire. */
function ReglageSansEffet({ r, raison }) {
  return (
    <div className="reglage reglage--inactif">
      <strong>{r.titre}</strong>
      <span className="petit texte-doux">{raison}</span>
    </div>
  );
}

function Reglage({ r, valeur, onChoisir }) {
  if (r.interrupteur) {
    return (
      <div className="reglage">
        <button
          className="reglage__ligne"
          role="switch"
          aria-checked={valeur ? "true" : "false"}
          onClick={() => onChoisir(r.cle, !valeur)}
        >
          <span className="reglage__texte">
            <strong>{r.titre}</strong>
            <span className="petit texte-doux">{r.aide}</span>
          </span>
          <Bascule actif={valeur} />
        </button>
      </div>
    );
  }
  return (
    <div className="reglage">
      <strong>{r.titre}</strong>
      <span className="petit texte-doux">{r.aide}</span>
      <div className="reglage__choix">
        {r.choix.map((c) => (
          <button
            key={c.valeur}
            className={`chip chip--petit${c.valeur === valeur ? " est-actif" : ""}`}
            onClick={() => onChoisir(r.cle, c.valeur)}
          >
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** L'état des alertes de l'appareil — lu à l'ouverture, relu après chaque geste. */
function useAlertes() {
  const [, forcer] = useState(0);
  const rafraichir = () => forcer((n) => n + 1);
  const autorisation = Alertes.autorisation();
  const activees = Alertes.activees();
  const libelle = autorisation === "denied" ? "refusées" : activees ? "activées" : "désactivées";
  return { activees, autorisation, libelle, rafraichir };
}

export default function Parametres({ ctx }) {
  const moi = ctx.moi;
  const compteDAccueil = (APP.decouverte?.membres || []).includes(moi?.uid);
  const [valeurs, setValeurs] = useState({
    ...CONFIDENTIALITE_DEFAUT,
    ...confidentialiteDe(moi),
  });
  const alertes = useAlertes();
  const [sons, setSons] = useState(sonsActifs);
  const minuteur = useRef(null);
  const enAttente = useRef(null);

  /* ⚠️ AU DÉPART DE L'ÉCRAN, ON DÉCLENCHE — ON N'ANNULE PLUS. 20 octobre
     2026, trouvé à l'audit : le départ se contentait d'un `clearTimeout`, et
     un réglage basculé moins de 250 ms avant de quitter était PERDU — l'écran
     avait montré le nouveau choix, la base gardait l'ancien, sans un mot.
     L'attente n'existe que pour regrouper des bascules rapprochées : quand on
     part, il n'y en aura plus — on enregistre tout de suite. */
  useEffect(
    () => () => {
      clearTimeout(minuteur.current);
      enAttente.current?.();
    },
    []
  );

  /**
   * Le membre ne valide rien : un réglage de confidentialité qu'on oublie de
   * valider est un piège. On enregistre donc tout seul, un court instant après
   * le geste — assez pour absorber plusieurs bascules d'affilée en une écriture.
   */
  const choisir = (cle, valeur) => {
    const avant = valeurs[cle];
    if (avant === valeur) return;
    const nouvelles = { ...valeurs, [cle]: valeur };
    setValeurs(nouvelles);

    clearTimeout(minuteur.current);
    const enregistrer = async () => {
      enAttente.current = null;
      try {
        await Store.users.update({ confidentialite: nouvelles });
        // On modifie `ctx.moi` sans repasser par majMoi : redessiner l'écran à
        // chaque bascule ferait sauter le bouton sous le doigt du membre.
        ctx.moi.confidentialite = { ...nouvelles };
        toast("Réglage enregistré");
      } catch (e) {
        setValeurs((v) => ({ ...v, [cle]: avant })); // on remet ce qu'on voyait
        toast(e.message || "Enregistrement impossible", "erreur");
      }
    };
    enAttente.current = enregistrer;
    minuteur.current = setTimeout(enregistrer, 250);
  };

  const basculerAlertes = async () => {
    if (Alertes.activees()) {
      Alertes.desactiver();
      toast("Alertes désactivées sur cet appareil");
    } else if (Alertes.autorisation() === "denied") {
      toast("Réautorisez les notifications dans les réglages de l'appareil", "erreur");
    } else {
      try {
        const r = await Alertes.demander();
        toast(
          r === "granted" ? "Alertes activées 🔔" : "Autorisation refusée",
          r === "granted" ? "info" : "erreur"
        );
      } catch (e) {
        toast(e.message, "erreur");
      }
    }
    alertes.rafraichir();
  };

  return (
    <div className="colonne">
      <div className="entete-page">
        <a className="icon-btn" href="#/menu" aria-label="Retour">
          <Icone nom="retour" taille={20} />
        </a>
        <h1 className="titre-page">Paramètres et confidentialité</h1>
      </div>

      <section className="carte">
        <h2 className="profil__titre">Votre compte</h2>
        <ul className="details">
          <li>
            <Icone nom="profil" taille={18} /> <span>Nom</span>
            <strong>{moi.nom || "—"}</strong>
          </li>
          <li>
            <Icone nom="envoyer" taille={18} /> <span>E-mail</span>
            <strong>
              {moi.email || "—"}{" "}
              {moi.emailVerifie === false && (
                <span className="etiquette etiquette--attente">non confirmé</span>
              )}
            </strong>
          </li>
          {moi.telephone && (
            <li>
              <Icone nom="messages" taille={18} /> <span>Téléphone</span>
              <strong>{moi.telephone}</strong>
            </li>
          )}
          <li>
            <Icone nom="membres" taille={18} /> <span>Église</span>
            <strong>{moi.eglise || "—"}</strong>
          </li>
          <li>
            <Icone nom="fil" taille={18} /> <span>Ville</span>
            <strong>{moi.ville || "—"}</strong>
          </li>
          <li>
            <Icone nom="verset" taille={18} /> <span>Baptême</span>
            <strong>{etatBapteme(moi)}</strong>
          </li>
        </ul>
        <button className="btn btn--gris btn--bloc" onClick={() => editerProfil(ctx)}>
          Modifier mes informations
        </button>
      </section>

      <section className="carte">
        <h2 className="profil__titre">Confidentialité</h2>
        <p className="petit texte-doux">
          Vous choisissez ce que vous montrez. Par défaut tout est ouvert : Koinonia ne
          referme jamais un profil à votre place.
        </p>
        <div>
          {REGLAGES.map((r) =>
            r.cle === "publications" && compteDAccueil ? (
              <ReglageSansEffet
                key={r.cle}
                r={r}
                raison="Votre compte fait partie de l'accueil de Koinonia : vos publications sont toujours ouvertes à tous, pour que les nouveaux membres aient quelque chose à lire dès leur arrivée."
              />
            ) : (
              <Reglage key={r.cle} r={r} valeur={valeurs[r.cle]} onChoisir={choisir} />
            )
          )}
        </div>
      </section>

      <section className="carte">
        <h2 className="profil__titre">Notifications</h2>
        <div className="menu-liste">
          {Alertes.supporte() ? (
            <button className="menu-item" onClick={basculerAlertes}>
              <Icone nom="alertes" taille={20} /> Alertes sur cet appareil{" "}
              <span className={`etat-alertes${alertes.activees ? " est-actif" : ""}`}>
                {alertes.libelle}
              </span>
            </button>
          ) : (
            <p className="petit texte-doux">Cet appareil ne sait pas afficher de notification.</p>
          )}

          {/* ⚠️ RÉGLAGE D'APPAREIL, PAS DE COMPTE. Comme les alertes juste
              au-dessus : on ne veut pas forcément la même chose sur son
              téléphone et sur l'ordinateur du bureau. Il ne part donc pas en
              base et ne suit pas le membre d'un écran à l'autre.

              ⚠️ ET LE FAIT DE L'ALLUMER JOUE LE SON. Un interrupteur de son
              muet est une devinette : on l'active, rien ne se passe, on ne sait
              pas si ça marche. */}
          <button
            className="menu-item"
            onClick={() => {
              const veut = !sons;
              reglerSons(veut);
              setSons(veut);
              if (veut) jouer("recu");
            }}
          >
            <Icone nom="messages" taille={20} /> Sons des messages{" "}
            <span className={`etat-alertes${sons ? " est-actif" : ""}`}>
              {sons ? "activés" : "désactivés"}
            </span>
          </button>
        </div>
        <p className="petit texte-doux" style={{ margin: "12px 0 0" }}>
          Un son court à l'envoi, à la réception et à l'arrivée d'une alerte. Ils
          restent discrets : cette application s'ouvre aussi pendant le culte.
        </p>
      </section>

      <section className="carte">
        <h2 className="profil__titre">Sécurité et connexion</h2>
        <div className="menu-liste">
          <button className="menu-item" onClick={changerMotDePasse}>
            <Icone nom="options" taille={20} /> Changer mon mot de passe
          </button>
          <button className="menu-item" onClick={() => ouvrirListeBloques(ctx)}>
            <Icone nom="fermer" taille={20} /> Membres bloqués
          </button>
        </div>
      </section>

      <section className="carte">
        <h2 className="profil__titre">Vos informations</h2>
        <div className="menu-liste">
          <button className="menu-item" onClick={() => exporterMesDonnees(ctx)}>
            <Icone nom="envoyer" taille={20} /> Télécharger mes données
          </button>
          <button className="menu-item menu-item--danger" onClick={() => supprimerMonCompte(ctx)}>
            <Icone nom="fermer" taille={20} /> Supprimer mon compte
          </button>
        </div>
        <p className="petit texte-doux" style={{ margin: "12px 0 0" }}>
          Ce que nous collectons, pourquoi, et comment le faire effacer :{" "}
          <a href="confidentialite.html" target="_blank" rel="noopener">
            politique de confidentialité
          </a>
          .
        </p>
      </section>

      <section className="carte avertissement">
        <Icone nom="alertes" taille={20} />
        <p className="petit">
          Ces réglages sont aujourd'hui appliqués par l'application sur chaque appareil. Ils
          ne deviendront des barrières qu'une fois les règles du serveur déployées. Ne
          publiez rien que vous ne voudriez pas voir lu.
        </p>
      </section>
    </div>
  );
}
